import { gsap } from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

const initHomeFAQAccordion = () => {
  const container = document.querySelector(".bt-faq-list");
  if (!container) return;

  const items = Array.from(container.querySelectorAll(".bt-faq-item"));
  if (!items.length) return;

  const isQA = document.documentElement.classList.contains("bt-qa-overlay");

  items.forEach((item) => {
    const trigger = item.querySelector(".bt-faq-item__trigger");
    const answer = item.querySelector(".bt-faq-item__answer");
    if (!trigger || !answer) return;

    const open = item.classList.contains("is-open");
    const itemStyles = getComputedStyle(item);
    const openPadding = parseFloat(itemStyles.paddingBottom) || 0;
    const closedPadding = parseFloat(itemStyles.paddingTop) || 0;
    const borderHeight = (parseFloat(itemStyles.borderTopWidth) || 0) + (parseFloat(itemStyles.borderBottomWidth) || 0);
    gsap.set(answer, { height: open ? "auto" : 0, opacity: open ? 1 : 0, overflow: "hidden" });
    gsap.set(item, { paddingBottom: open ? openPadding : closedPadding });

    trigger.addEventListener("click", () => {
      const shouldOpen = !item.classList.contains("is-open");
      item.classList.toggle("is-open", shouldOpen);
      trigger.setAttribute("aria-expanded", String(shouldOpen));
      gsap.killTweensOf([item, answer]);
      const paddingTop = parseFloat(getComputedStyle(item).paddingTop) || 0;
      const triggerHeight = trigger.offsetHeight;

      if (isQA) {
        gsap.set(item, { height: "auto", paddingBottom: shouldOpen ? openPadding : closedPadding });
        gsap.set(answer, { height: shouldOpen ? "auto" : 0, opacity: shouldOpen ? 1 : 0 });
        return;
      }

      if (shouldOpen) {
        const answerHeight = answer.scrollHeight;
        const openHeight = paddingTop + triggerHeight + openPadding + answerHeight + borderHeight;
        gsap.set(item, { height: item.offsetHeight, paddingBottom: closedPadding });
        gsap.fromTo(answer, { height: 0, opacity: 0 }, {
          height: answerHeight,
          opacity: 1,
          duration: 0.45,
          ease: "power2.out",
          overwrite: "auto",
        });
        gsap.to(item, { height: openHeight, paddingBottom: openPadding, duration: 0.45, ease: "power2.out", overwrite: "auto", onComplete: () => gsap.set(item, { height: "auto" }) });
      } else {
        const closedHeight = paddingTop + triggerHeight + closedPadding + borderHeight;
        gsap.fromTo(answer, { height: answer.offsetHeight, opacity: 1 }, {
          height: 0,
          opacity: 0,
          duration: 0.35,
          ease: "power2.inOut",
          overwrite: "auto",
        });
        gsap.to(item, { height: closedHeight, paddingBottom: closedPadding, duration: 0.35, ease: "power2.inOut", overwrite: "auto" });
      }
    });
  });
};

const initGroupedFAQAccordion = () => {
  const container = document.querySelector(".grouped-faq-accordion");
  if (!container) return;

  const navItems = Array.from(container.querySelectorAll(".nav-item"));
  const groups = Array.from(container.querySelectorAll(".faq-group"));

  if (!navItems.length || !groups.length) return;

  let activeIndex = 0;
  let isSwitching = false;

  // =============================
  // INITIAL STATE (SECTIONS)
  // =============================

  gsap.set(groups, { display: "none", opacity: 0, y: 40 });

  // =============================
  // SECTION SWITCH
  // =============================

  const showGroup = (index) => {
    const group = groups[index];
    if (!group) return;

    gsap.set(group, { display: "block" });

    return gsap
      .timeline({
        defaults: { ease: "power2.out" },
        onComplete: () => {
          isSwitching = false;
          initAccordion(group);
        },
      })
      .to(group, {
        opacity: 1,
        y: 0,
        duration: 0.5,
      })
      .from(
        group.querySelectorAll(".faq-item"),
        {
          opacity: 0,
          y: 30,
          duration: 0.4,
          stagger: 0.08,
        },
        "-=0.3",
      );
  };

  const hideGroup = (index) => {
    const group = groups[index];
    if (!group) return Promise.resolve();

    return gsap.to(group, {
      opacity: 0,
      y: 40,
      duration: 0.3,
      ease: "power2.in",
      onComplete: () => {
        gsap.set(group, { display: "none" });
      },
    });
  };

  const activate = (index) => {
    if (index === activeIndex || isSwitching) return;

    isSwitching = true;

    navItems.forEach((nav) => nav.classList.remove("active"));
    navItems[index]?.classList.add("active");

    hideGroup(activeIndex).then(() => {
      showGroup(index);
    });

    activeIndex = index;
  };

  // default first section
  navItems[0]?.classList.add("active");
  showGroup(0);

  navItems.forEach((nav, i) => {
    nav.addEventListener("click", () => activate(i));
  });

  // =============================
  // ACCORDION (PURE GSAP)
  // =============================

  function initAccordion(groupElement) {
    if (!groupElement) return;

    const items = Array.from(groupElement.querySelectorAll(".faq-item"));

    if (!items.length) return;

    // FORCE CLOSE ALL
    items.forEach((item) => {
      const answer = item.querySelector(".answer");
      const icon = item.querySelector(".icon");

      item.classList.remove("is-open");
      icon?.classList.remove("active");

      if (answer) {
        gsap.killTweensOf(answer);
        gsap.set(answer, {
          height: 0,
          opacity: 0,
          overflow: "hidden",
        });
      }
    });

    // OPEN FIRST ONLY
    openItem(items[0]);

    // CLICK HANDLER
    items.forEach((item) => {
      const summary = item.querySelector(".summary");
      if (!summary) return;

      summary.onclick = () => {
        const isOpen = item.classList.contains("is-open");

        items.forEach((el) => {
          if (el !== item) closeItem(el);
        });

        if (!isOpen) {
          openItem(item);
        } else {
          closeItem(item);
        }

        ScrollTrigger.refresh();
      };
    });
  }

  function openItem(item) {
    const answer = item.querySelector(".answer");
    const icon = item.querySelector(".icon");

    if (!answer) return;

    item.classList.add("is-open");
    icon?.classList.add("active");

    gsap.killTweensOf(answer);

    const height = answer.scrollHeight;

    gsap.fromTo(
      answer,
      { height: 0, opacity: 0 },
      {
        height: height,
        opacity: 1,
        duration: 0.45,
        ease: "power2.out",
        overwrite: "auto",
        onComplete: () => {
          gsap.set(answer, { height: "auto" });
          ScrollTrigger.refresh();
        },
      },
    );
  }

  function closeItem(item) {
    const answer = item.querySelector(".answer");
    const icon = item.querySelector(".icon");

    if (!answer || !item.classList.contains("is-open")) return;

    item.classList.remove("is-open");
    icon?.classList.remove("active");

    gsap.killTweensOf(answer);

    const height = answer.scrollHeight;

    gsap.fromTo(
      answer,
      { height: height, opacity: 1 },
      {
        height: 0,
        opacity: 0,
        duration: 0.4,
        ease: "power2.inOut",
        overwrite: "auto",
      },
    );
  }
};

document.addEventListener("DOMContentLoaded", initGroupedFAQAccordion);
document.addEventListener("DOMContentLoaded", initHomeFAQAccordion);
